import { createClient } from '@supabase/supabase-js';
import { auth, functions } from '../firebase'; // Import pre-configured functions client
import { httpsCallable, httpsCallableFromURL } from 'firebase/functions';
import { setDiagTokenSource } from '../utils/presensiDiagnostics';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
}

// Token Cache (localStorage, terikat UID) & In-flight Deduplication
//
// Token disimpan di localStorage — BUKAN sessionStorage — supaya TETAP ADA
// ketika aplikasi/PWA ditutup lalu dibuka lagi. Dulu (sessionStorage) token
// hilang setiap aplikasi ditutup oleh HP (sering terjadi di HP dengan RAM
// kecil), sehingga saat presensi aplikasi harus memanggil Cloud Function
// createSupabaseToken dulu (beberapa detik di data seluler) sebelum bisa
// menyimpan. Token diikat ke UID pemiliknya dan dihapus saat logout.
const TOKEN_STORE_KEY = 'supabase_token_store_v1';
// Kunci lama (sessionStorage) — hanya untuk dibersihkan.
const LEGACY_SESSION_TOKEN_KEY = 'supabase_cached_token';
const LEGACY_SESSION_EXPIRY_KEY = 'supabase_token_expiry';

// Sisa masa berlaku token yang memicu penyegaran proaktif di latar belakang.
// Token server berlaku 24 jam; menyegarkan saat sisa < 12 jam memastikan
// token SELALU segar saat tombol presensi ditekan, sehingga pengguna di data
// seluler tidak pernah menunggu pemanggilan Cloud Function di momen submit.
const TOKEN_REFRESH_AHEAD_MS = 12 * 60 * 60 * 1000;

// Token juga disegarkan di latar belakang bila UMURNYA sudah > 12 jam (dibaca
// dari klaim `iat`). Dengan token berumur panjang (server: 6 hari) presensi
// tidak lagi bergantung pada Cloud Function di momen submit: penyegaran yang
// gagal di data seluler tidak masalah karena token lama masih berlaku berhari-hari.
const TOKEN_REFRESH_AFTER_AGE_MS = 12 * 60 * 60 * 1000;

// Buffer keamanan: anggap token "hampir expired" 5 menit sebelum exp aslinya.
const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000;

// Batas waktu total & per-percobaan untuk memanggil Cloud Function pembuat
// token, supaya jalur presensi tidak menggantung lama di jaringan lemah.
//
// Budget dinaikkan dari 8 → 15 detik karena kini ada 2 rute (proxy domain
// aplikasi, lalu langsung ke cloudfunctions.net) dan pengambilan token dimulai
// sejak alur presensi dibuka, sehingga berjalan bersamaan dengan pencarian GPS.
const TOKEN_FETCH_BUDGET_MS = 15000;
const TOKEN_FETCH_ATTEMPT_TIMEOUT_MS = 7000;

// Rute pemanggilan Cloud Function `createSupabaseToken`.
// 1. 'proxy'    → `/api/fn/createSupabaseToken` di domain aplikasi sendiri
//                 (Vercel meneruskan ke Cloud Function, lihat api/fn/[name].js).
//                 Di sebagian jaringan data seluler, HP tidak bisa menjangkau
//                 `*.cloudfunctions.net` (permintaan menggantung sampai timeout)
//                 padahal domain aplikasi jelas bisa (aplikasinya terbuka).
// 2. 'langsung' → `*.cloudfunctions.net` seperti sebelumnya (cadangan, mis. saat
//                 dijalankan di localhost / proxy belum ter-deploy).
type TokenRoute = 'proxy' | 'langsung';
const TOKEN_FUNCTION_NAME = 'createSupabaseToken';

const isLocalHost = (): boolean => {
    try {
        return /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
    } catch {
        return true;
    }
};

const getTokenRoutes = (): TokenRoute[] => (isLocalHost() ? ['langsung'] : ['proxy', 'langsung']);

const callTokenFunction = async (route: TokenRoute): Promise<{ token: string }> => {
    const callable = route === 'proxy'
        ? httpsCallableFromURL<object, { token: string }>(functions, `${window.location.origin}/api/fn/${TOKEN_FUNCTION_NAME}`)
        // 'functions' is already initialized with 'asia-southeast2' region in firebase.ts
        : httpsCallable<object, { token: string }>(functions, TOKEN_FUNCTION_NAME);
    const result = await callable({});
    if (!result?.data?.token) throw new Error('EMPTY_TOKEN');
    return result.data;
};

// Grace period: jika Cloud Function tak terjangkau, token cache yang expired
// belum terlalu lama TETAP dipakai (server Supabase masih menerimanya karena
// exp asli JWT = 24 jam, lebih panjang dari taksiran client 23 jam).
const TOKEN_GRACE_PERIOD_MS = 60 * 60 * 1000; // 60 menit

let cachedSupabaseToken: string | null = null;
let tokenExpiry: number | null = null;
let cachedTokenUid: string | null = null;
let inflightTokenPromise: Promise<string | null> | null = null;

const persistToken = (uid: string, token: string, expiry: number): void => {
    try {
        localStorage.setItem(TOKEN_STORE_KEY, JSON.stringify({ uid, token, exp: expiry }));
    } catch {
        // Abaikan (mis. mode incognito / storage penuh) — cache memori tetap jalan
    }
};

/** Muat token tersimpan ke memori HANYA jika milik user yang sedang login. */
const loadStoredToken = (uid: string): boolean => {
    try {
        const raw = localStorage.getItem(TOKEN_STORE_KEY);
        if (!raw) return false;
        const stored = JSON.parse(raw) as { uid?: string; token?: string; exp?: number };
        if (stored.uid !== uid || !stored.token || typeof stored.exp !== 'number') return false;
        cachedSupabaseToken = stored.token;
        tokenExpiry = stored.exp;
        cachedTokenUid = uid;
        return true;
    } catch {
        return false;
    }
};

/** Hapus token Supabase dari memori & penyimpanan. Dipanggil saat logout. */
export const clearSupabaseTokenCache = (): void => {
    cachedSupabaseToken = null;
    tokenExpiry = null;
    cachedTokenUid = null;
    try { localStorage.removeItem(TOKEN_STORE_KEY); } catch { /* noop */ }
};

// Bersihkan sisa penyimpanan lama (sessionStorage) dari versi sebelumnya.
try {
    sessionStorage.removeItem(LEGACY_SESSION_TOKEN_KEY);
    sessionStorage.removeItem(LEGACY_SESSION_EXPIRY_KEY);
} catch { /* noop */ }

/** Pastikan cache memori milik user yang sedang login (ganti akun → buang). */
const syncCacheOwner = (): string | null => {
    const uid = auth.currentUser?.uid || null;
    if (!uid) return null;
    if (cachedTokenUid !== uid) {
        cachedSupabaseToken = null;
        tokenExpiry = null;
        cachedTokenUid = null;
        loadStoredToken(uid);
    }
    return uid;
};

/**
 * Baca klaim `exp` asli dari JWT (payload base64url).
 * Nilai kembali dalam milidetik, atau null jika gagal di-decode.
 */
const decodeJwtClaim = (token: string, claim: 'exp' | 'iat'): number | null => {
    try {
        const part = token.split('.')[1];
        if (!part) return null;
        const padded = part.replace(/-/g, '+').replace(/_/g, '/');
        const json = JSON.parse(atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '=')));
        return typeof json[claim] === 'number' ? json[claim] * 1000 : null;
    } catch {
        return null;
    }
};

const decodeJwtExp = (token: string): number | null => decodeJwtClaim(token, 'exp');

// Jeda sebelum rute cadangan ikut dijalankan bila rute pertama belum menjawab.
const TOKEN_HEDGE_DELAY_MS = 3000;

/**
 * Minta token lewat beberapa rute dengan "hedging" (lihat getSupabaseToken).
 * Resolve dengan hasil pertama yang berhasil; reject bila semua rute gagal
 * atau `timeoutMs` habis. Rute yang gagal/timeout dicatat di `failed`.
 */
const fetchTokenHedged = (
    routes: TokenRoute[],
    timeoutMs: number,
    failed: Set<TokenRoute>
): Promise<{ token: string; route: TokenRoute }> =>
    new Promise((resolve, reject) => {
        let started = 0;
        let pending = 0;
        let settled = false;
        let lastError: unknown = new Error('timeout');
        const inFlight = new Set<TokenRoute>();

        const finish = (err: unknown, value?: { token: string; route: TokenRoute }) => {
            if (settled) return;
            settled = true;
            clearTimeout(overallTimer);
            clearTimeout(hedgeTimer);
            if (err) {
                inFlight.forEach(r => failed.add(r));
                reject(err);
            } else {
                resolve(value!);
            }
        };

        const startNext = () => {
            if (settled || started >= routes.length) return;
            const route = routes[started++];
            pending++;
            inFlight.add(route);
            callTokenFunction(route).then(
                data => finish(null, { token: data.token, route }),
                err => {
                    pending--;
                    inFlight.delete(route);
                    failed.add(route);
                    lastError = err;
                    console.warn(`Supabase token via ${route} gagal:`, err);
                    if (started < routes.length) startNext();
                    else if (pending === 0) finish(err);
                }
            );
        };

        const overallTimer = setTimeout(() => finish(lastError instanceof Error && lastError.message !== 'timeout' ? lastError : new Error('timeout')), timeoutMs);
        const hedgeTimer = setTimeout(startNext, TOKEN_HEDGE_DELAY_MS);
        startNext();
    });

export const isSupabaseTokenReady = (): boolean => {
    syncCacheOwner();
    return !!(cachedSupabaseToken && tokenExpiry && Date.now() < (tokenExpiry - TOKEN_EXPIRY_BUFFER_MS));
};

// ============================================================================
// LATAR BELAKANG: penyegaran token proaktif
// ============================================================================
// Token Supabase berlaku 24 jam. Kalau client baru mengambil token ketika
// tombol presensi ditekan, pengguna di data seluler akan menunggu Cloud
// Function (yang bisa gagal di jaringan lemah) persis di momen paling penting.
// Untuk itu token disegarkan di latar belakang setiap kali aplikasi terbuka
// dan sisa masa berlakunya < TOKEN_REFRESH_AHEAD_MS.
let backgroundRefreshPromise: Promise<string | null> | null = null;

/**
 * Pastikan token yang tersimpan masih segar. Dipanggil oleh AuthContext saat
 * login (sudah ada) dan oleh usePresensiSubmit sebelum submit presensi —
 * keduanya NON-BLOCKING sehingga tidak menambah loading di UI.
 */
export const ensureSupabaseTokenFresh = (): Promise<string | null> => {
    if (backgroundRefreshPromise) return backgroundRefreshPromise;
    syncCacheOwner();
    if (!cachedSupabaseToken || !tokenExpiry) {
        return getSupabaseToken().catch(() => null);
    }

    const timeLeft = tokenExpiry - TOKEN_EXPIRY_BUFFER_MS - Date.now();
    const issuedAt = decodeJwtClaim(cachedSupabaseToken, 'iat');
    const isOld = issuedAt !== null && Date.now() - issuedAt > TOKEN_REFRESH_AFTER_AGE_MS;
    if (timeLeft > TOKEN_REFRESH_AHEAD_MS && !isOld) {
        // Token masih sangat segar — tidak perlu refresh.
        return Promise.resolve(cachedSupabaseToken);
    }

    backgroundRefreshPromise = getSupabaseToken(true)
        .catch(() => cachedSupabaseToken)
        .finally(() => { backgroundRefreshPromise = null; });
    return backgroundRefreshPromise;
};

export const getSupabaseToken = async (force = false, retries = 2, delay = 400): Promise<string | null> => {
    const user = auth.currentUser;
    if (!user) return null;

    // 1. Token valid di memori / localStorage (milik user ini)
    const wasInMemory = cachedTokenUid === user.uid && !!cachedSupabaseToken;
    if (!force && isSupabaseTokenReady() && cachedSupabaseToken) {
        setDiagTokenSource(wasInMemory ? 'memori' : 'tersimpan');
        return cachedSupabaseToken;
    }

    // 2. If a request is already in-flight, reuse the same promise (Deduplication)
    if (inflightTokenPromise) {
        return inflightTokenPromise;
    }

    // 3. Initiate new token fetch request with deduplication
    setDiagTokenSource('cloud-function');
    inflightTokenPromise = (async () => {
        const deadline = Date.now() + TOKEN_FETCH_BUDGET_MS;
        try {
            // Setiap putaran memakai "hedged request": rute pertama (proxy) dicoba,
            // dan bila belum menjawab dalam TOKEN_HEDGE_DELAY_MS (atau gagal), rute
            // berikutnya ikut dijalankan. Yang pertama berhasil dipakai. Jadi di
            // jaringan yang tidak bisa menjangkau salah satu host, tidak perlu
            // menunggu timeout penuh sebelum mencoba host lain.
            const routes = getTokenRoutes();
            const failedRoutes = new Set<TokenRoute>();
            for (let round = 0; round < Math.max(1, retries); round++) {
                const timeLeft = deadline - Date.now();
                if (timeLeft <= 0) break;
                try {
                    const { token, route } = await fetchTokenHedged(
                        routes,
                        Math.min(TOKEN_FETCH_ATTEMPT_TIMEOUT_MS * routes.length, timeLeft),
                        failedRoutes
                    );
                    setDiagTokenSource(`cloud-function (${route})`, true);

                    // Baca exp ASLI dari JWT (server menerbitkan token berumur panjang).
                    // Fallback ke 23 jam hanya jika decode gagal.
                    const realExp = decodeJwtExp(token);
                    const expiry = realExp ?? (Date.now() + (23 * 60 * 60 * 1000));
                    cachedSupabaseToken = token;
                    tokenExpiry = expiry;
                    cachedTokenUid = user.uid;
                    persistToken(user.uid, token, expiry);

                    return token;
                } catch (error) {
                    console.warn(`Error fetching Custom Supabase Token (round ${round + 1}):`, error);
                    const remaining = deadline - Date.now();
                    if (round < retries - 1 && remaining > 0) {
                        await new Promise(res => setTimeout(res, Math.min(delay, remaining)));
                    }
                }
            }
            if (failedRoutes.size > 0) {
                setDiagTokenSource(`cloud-function (gagal: ${Array.from(failedRoutes).join(', ')})`, true);
            }

            // GRACE PERIOD: jika semua retry gagal, cek apakah masih ada token cache
            // yang expired-nya belum terlalu lama (≤ 60 menit lewat expiry KLIEN).
            // Token JWT asli berlaku 24 jam, sehingga token yang dianggap expired
            // oleh client masih diterima server → jauh lebih baik daripada gagal
            // total di jaringan lemah.
            if (cachedSupabaseToken && tokenExpiry && Date.now() < (tokenExpiry + TOKEN_GRACE_PERIOD_MS)) {
                console.warn(
                    `Cloud Function unreachable, using cached Supabase token as grace-period fallback ` +
                    `(expired ${Math.round((Date.now() - tokenExpiry) / 1000)}s ago)`
                );
                return cachedSupabaseToken;
            }

            // Tidak ada token cache (atau sudah expired > 60 menit) dan semua retry gagal → blokir
            console.error("Failed to fetch Supabase token after all retries and no valid cache.");
            throw new Error('AUTH_TOKEN_MISSING');
        } finally {
            inflightTokenPromise = null;
        }
    })();

    return inflightTokenPromise;
};

// Segarkan token setiap kali aplikasi kembali dibuka/ke depan (PWA di HP
// sering hanya "dilanjutkan", bukan dimuat ulang). Pagi hari saat aplikasi
// dibuka, token sudah disegarkan di latar belakang sebelum tombol presensi
// ditekan.
try {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && auth.currentUser) {
            ensureSupabaseTokenFresh().catch(() => null);
        }
    });
} catch { /* noop (non-browser) */ }

const abortError = (): Error => {
    try {
        return new DOMException('The operation was aborted.', 'AbortError');
    } catch {
        const e = new Error('The operation was aborted.');
        e.name = 'AbortError';
        return e;
    }
};

/** Tunggu `promise`, tapi berhenti segera bila `signal` dibatalkan (timeout permintaan). */
const raceAbort = <T,>(promise: Promise<T>, signal?: AbortSignal | null): Promise<T> => {
    if (!signal) return promise;
    if (signal.aborted) return Promise.reject(abortError());
    return new Promise<T>((resolve, reject) => {
        const onAbort = () => reject(abortError());
        signal.addEventListener('abort', onAbort, { once: true });
        promise.then(
            v => { signal.removeEventListener('abort', onAbort); resolve(v); },
            e => { signal.removeEventListener('abort', onAbort); reject(e); }
        );
    });
};

// Create a single Supabase client that dynamically attaches the Custom JWT to requests.
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  global: {
    fetch: async (url, options = {}) => {
      // 1. Ambil JWT Supabase (dengan grace-period fallback untuk internet lemah).
      //
      // - Menghormati `abortSignal` permintaan: dulu batas waktu (mis. cek izin
      //   8 dtk) tidak berlaku selama menunggu token, sehingga bisa menggantung.
      // - Bila token gagal didapat, kembalikan respons 401 alih-alih melempar
      //   error. Error yang dilempar dianggap "gangguan jaringan" oleh
      //   postgrest-js → permintaan GET diulang 3× (jeda 1+2+4 dtk) dan setiap
      //   pengulangan meminta token lagi ke Cloud Function → cek izin di
      //   diagnosis bisa mencapai 18–23 dtk.
      let token: string | null = null;
      try {
        token = await raceAbort(getSupabaseToken(), options.signal);
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') throw err;
        return new Response(
          JSON.stringify({ code: 'AUTH_TOKEN_MISSING', message: 'AUTH_TOKEN_MISSING', details: null, hint: null }),
          { status: 401, headers: { 'Content-Type': 'application/json' } }
        );
      }

      // 2. Override the Authorization Bearer header securely by safely merging Header objects
      if (token) {
        const headers = new Headers(options.headers);
        headers.set('Authorization', `Bearer ${token}`);
        options.headers = headers;
      }

      return fetch(url, options);
    },
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

/**
 * Sinkronkan JWT token ke Supabase Realtime WebSocket.
 * 
 * WAJIB dipanggil sebelum subscribe ke channel real-time (postgres_changes).
 * Tanpa ini, koneksi WebSocket real-time hanya pakai anon key —
 * RLS bisa menolak dan subscription gak akan menerima update.
 */
export const ensureRealtimeAuth = async (): Promise<void> => {
    try {
        const token = await getSupabaseToken();
        if (token && supabase.realtime) {
            supabase.realtime.setAuth(token);
            console.log('[Supabase Realtime] ✅ Auth token synced to WebSocket');
        } else if (!token) {
            console.log('[Supabase Realtime] ⚠️ No user logged in, using anon key for realtime');
        }
    } catch (e) {
        console.warn('[Supabase Realtime] ❌ Failed to sync auth token:', e);
    }
};
